import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { Plus, Pencil, Trash2, BarChart3, Search, RefreshCw, Upload, Download, ShieldOff, Unlock } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import DatePicker from "@/components/DatePicker";

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);

function genPwd() {
  const c = "abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789@#";
  return Array.from({ length: 10 }, () => c[Math.floor(Math.random() * c.length)]).join("");
}

const emptyForm = () => ({
  first_name: "", last_name: "", email: "",
  password: genPwd(), from_page: 1, to_page: 50,
  start_date: today(), last_date: inDays(30), show_result: true,
  project_id: "project_1",
});

export default function AdminEmployees() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("active");         // "active" | "expired"
  const [counts, setCounts] = useState({ active: 0, expired: 0, total: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [projects, setProjects] = useState([]);
  const fileRef = useRef(null);
  const limit = 25;

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/employees", { params: { search, skip, limit, status: tab } });
    setRows(data.items); setTotal(data.total);
  }, [search, skip, tab]);

  const loadCounts = useCallback(async () => {
    try { const r = await api.get("/admin/employees/status-counts"); setCounts(r.data); }
    catch (e) { /* silent */ }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadCounts(); }, [loadCounts, tab, rows.length]);
  // Load projects once for the dropdown; also refresh when the create modal opens.
  useEffect(() => {
    (async () => {
      try { const r = await api.get("/admin/projects"); setProjects(r.data || []); } catch (e) { /* silent */ }
    })();
  }, [open]);

  const save = async () => {
    try {
      const payload = { ...form, from_page: +form.from_page, to_page: +form.to_page };
      if (editing) {
        if (!payload.password) delete payload.password;
        try {
          await api.put(`/admin/employees/${editing}`, payload);
        } catch (err) {
          // Backend refuses progress-affecting changes unless `force=true`.
          const d = err?.response?.data?.detail;
          if (err?.response?.status === 409 && d?.code === "progress_at_risk") {
            if (!window.confirm(`⚠️ ${d.message}\n\nDo you really want to proceed? The candidate's completed pages will become hidden to them.`)) {
              return;
            }
            await api.put(`/admin/employees/${editing}?force=true`, payload);
          } else {
            throw err;
          }
        }
      } else {
        await api.post("/admin/employees", payload);
      }
      toast.success(editing ? "Updated" : "Created. Email sent.");
      setOpen(false); setForm(emptyForm()); setEditing(null); load();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Failed");
    }
  };
  const del = async (id, name) => {
    const label = name ? ` "${name}"` : "";
    if (!window.confirm(`Are you sure you want to delete this employee${label}? This action cannot be undone.`)) return;
    // Optimistic UI: remove row immediately
    const prev = rows;
    setRows((r) => r.filter((x) => x.id !== id));
    setTotal((t) => Math.max(0, t - 1));
    try {
      try {
        await api.delete(`/admin/employees/${id}`);
      } catch (err) {
        const d = err?.response?.data?.detail;
        if (err?.response?.status === 409 && d?.code === "progress_at_risk") {
          if (!window.confirm(`⚠️ ${d.message}\n\nType-check: are you absolutely sure you want to permanently delete this candidate and all their ${d.existing_submissions} completed pages?`)) {
            setRows(prev);
            setTotal((t) => t + 1);
            return;
          }
          await api.delete(`/admin/employees/${id}?force=true`);
        } else {
          throw err;
        }
      }
      toast.success("Employee deleted successfully.");
      load(); // reconcile with server (pagination counts, etc.)
    } catch (err) {
      // Rollback optimistic update
      setRows(prev);
      setTotal((t) => t + 1);
      const d = err?.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || err?.message || "Failed to delete employee. Please try again.");
      toast.error(msg);
      console.error("Employee delete failed:", err);
    }
  };

  const unlock = async (id) => {
    await api.post(`/admin/employees/${id}/unlock`); toast.success("Account unlocked"); load();
  };

  const downloadTemplate = () => {
    const csv = "first_name,last_name,email,from_page,to_page,start_date,last_date,show_result,password\nRavi,Kumar,ravi@example.com,1,100,2026-02-01,2026-03-01,yes,\nPriya,Sharma,priya@example.com,101,200,2026-02-01,2026-03-01,yes,\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "employees_template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const token = localStorage.getItem("dw_token");
      const r = await fetch(`${API}/admin/employees/bulk-csv`, {
        method: "POST", body: fd, headers: { Authorization: `Bearer ${token}` }
      });
      const data = await r.json();
      setBulkResult(data);
      toast.success(`Created ${data.created_count}, ${data.errors?.length || 0} errors`);
      load();
    } catch (err) { toast.error("Upload failed"); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-4xl font-bold tracking-tight">{t("employees_title")}</h1>
          <p className="text-zinc-500 mt-2 text-sm">{t("total")}: {counts.total}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
            <input data-testid="emp-search" placeholder={`${t("search")}…`} value={search} onChange={(e) => { setSkip(0); setSearch(e.target.value); }}
              className="pl-9 pr-3 py-2.5 border border-[var(--line)] rounded-full text-sm bg-white" />
          </div>
          <button data-testid="bulk-import-btn" onClick={() => { setBulkOpen(true); setBulkResult(null); }} className="btn-ghost"><Upload size={16} /> {t("bulk_import")}</button>
          <button data-testid="add-employee-btn" onClick={() => { setForm(emptyForm()); setEditing(null); setOpen(true); }}
            className="btn-primary"><Plus size={16} /> {t("add_employee")}</button>
        </div>
      </div>

      {/* Active / Expired tabs */}
      <div className="flex gap-2 mb-4 border-b border-[var(--line)]">
        {[
          { key: "active",  label: "Active",  count: counts.active },
          { key: "expired", label: "Expired", count: counts.expired },
        ].map((it) => {
          const on = tab === it.key;
          return (
            <button
              key={it.key}
              data-testid={`emp-tab-${it.key}`}
              onClick={() => { setTab(it.key); setSkip(0); }}
              className={`relative px-4 py-2 text-sm font-semibold transition-colors ${on ? "text-[var(--ink)]" : "text-zinc-500 hover:text-zinc-800"}`}
            >
              {it.label}
              <span className={`ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-mono ${on ? "bg-rose-100 text-rose-700" : "bg-zinc-100 text-zinc-600"}`}>{it.count}</span>
              {on && <span className="absolute -bottom-px left-0 right-0 h-[2px] bg-[var(--accent)] rounded-t" />}
            </button>
          );
        })}
      </div>

      <div className="card-clean overflow-x-auto">
        <table className="w-full table-clean">
          <thead className="bg-zinc-50">
            <tr>
              <th className="text-left px-3 py-3 whitespace-nowrap">{t("name")}</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">Project</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">Progress</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">Range</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">{t("email")}</th>
              <th className="text-left px-3 py-3 whitespace-nowrap">Dates</th>
              <th className="sticky right-0 z-10 bg-zinc-50 text-right px-3 py-3 whitespace-nowrap shadow-[-6px_0_10px_-6px_rgba(0,0,0,0.08)]">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-[var(--line)] hover:bg-zinc-50">
                <td className="px-3 py-3 whitespace-nowrap">
                  <div className="font-medium flex items-center gap-2">
                    {r.first_name} {r.last_name}
                    {r.locked && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-200 text-[10px] font-semibold"><ShieldOff size={10} /> LOCKED</span>}
                  </div>
                </td>
                <td className="px-3 py-3">
                  <span
                    data-testid={`emp-project-${r.id}`}
                    className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-rose-50 text-rose-700 border border-rose-100 text-[11px] font-semibold"
                    title={r.project_id || "project_1"}
                  >
                    {r.project_name || "Project 1"}
                  </span>
                </td>
                <td className="px-3 py-3 whitespace-nowrap text-sm font-mono">
                  <span className="font-semibold">{r.completed_pages}</span>
                  <span className="text-zinc-400"> / {r.total_pages}</span>
                </td>
                <td className="px-3 py-3 text-xs font-mono text-zinc-600 whitespace-nowrap">{r.from_page}–{r.to_page}</td>
                <td className="px-3 py-3 text-sm">
                  <span className="block max-w-[220px] truncate" title={r.email}>{r.email}</span>
                </td>
                <td className="px-3 py-3 text-[11px] font-mono whitespace-nowrap leading-tight">
                  <div className="text-zinc-500">{r.start_date}</div>
                  <div className={tab === "expired" ? "text-rose-700 font-semibold" : "text-zinc-700"}>{r.last_date}</div>
                </td>
                <td className="sticky right-0 z-10 bg-white hover:bg-zinc-50 px-3 py-3 shadow-[-6px_0_10px_-6px_rgba(0,0,0,0.08)]">
                  <div className="flex gap-1 justify-end">
                    {r.locked && <button onClick={() => unlock(r.id)} data-testid={`emp-unlock-${r.id}`} className="p-1.5 rounded hover:bg-emerald-50 text-emerald-700" title="Unlock"><Unlock size={14} /></button>}
                    <Link to={`/admin/employees/${r.id}/result`} data-testid={`emp-result-${r.id}`} className="p-1.5 rounded hover:bg-zinc-100" title="Result"><BarChart3 size={14} /></Link>
                    <button onClick={() => { setEditing(r.id); setForm({ ...r, password: "" }); setOpen(true); }} className="p-1.5 rounded hover:bg-zinc-100" title="Edit"><Pencil size={14} /></button>
                    <button onClick={() => del(r.id, `${r.first_name} ${r.last_name}`)} data-testid={`emp-delete-${r.id}`} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Delete employee"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">
              {tab === "expired" ? "No expired employees." : "No active employees."}
            </td></tr>}
          </tbody>
        </table>
      </div>
      <div className="flex justify-between items-center mt-4 text-sm">
        <button disabled={!skip} onClick={() => setSkip(Math.max(0, skip - limit))} className="btn-ghost disabled:opacity-50">{t("prev")}</button>
        <div>{Math.floor(skip / limit) + 1} / {Math.max(1, Math.ceil(total / limit))}</div>
        <button disabled={skip + limit >= total} onClick={() => setSkip(skip + limit)} className="btn-ghost disabled:opacity-50">{t("next")}</button>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)}>
          <div className="card-clean p-8 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-6">{editing ? t("edit") : t("add")} {t("employees")}</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["first_name", t("first_name")], ["last_name", t("last_name")], ["email", t("email")]].map(([k, l]) => (
                <div key={k} className={k === "email" ? "col-span-2" : ""}>
                  <label className="text-xs uppercase tracking-widest text-zinc-500">{l}</label>
                  <input data-testid={`emp-${k}`} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                    className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5" />
                </div>
              ))}
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("password")} {editing && t("blank_keep")}</label>
                <div className="flex gap-2 mt-1">
                  <input data-testid="emp-password" type="text" value={form.password || ""} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="flex-1 border border-[var(--line)] rounded-lg px-3 py-2.5 font-mono text-sm" />
                  <button type="button" onClick={() => setForm({ ...form, password: genPwd() })} className="btn-ghost text-sm"><RefreshCw size={14} /> {t("generate")}</button>
                </div>
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">Project</label>
                <select
                  data-testid="emp-project"
                  value={form.project_id || "project_1"}
                  onChange={(e) => setForm({ ...form, project_id: e.target.value })}
                  className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5 bg-white"
                >
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.actual_page_count} pages
                    </option>
                  ))}
                  {projects.length === 0 && <option value="project_1">Project 1 · default</option>}
                </select>
                <div className="text-[11px] text-zinc-500 mt-1">Each project has its own unique random content. Manage from Admin → Projects.</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("from_page")}</label>
                <input data-testid="emp-from" type="number" min={1} max={1500} value={form.from_page} onChange={(e) => setForm({ ...form, from_page: e.target.value })} className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("to_page")} (max 500)</label>
                <input data-testid="emp-to" type="number" min={1} max={1500} value={form.to_page} onChange={(e) => setForm({ ...form, to_page: e.target.value })} className="mt-1 w-full border border-[var(--line)] rounded-lg px-3 py-2.5" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("start_date")}</label>
                <div className="mt-1"><DatePicker testId="emp-start" value={form.start_date} onChange={(d) => setForm({ ...form, start_date: d })} /></div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("last_date")}</label>
                <div className="mt-1"><DatePicker testId="emp-last" value={form.last_date} onChange={(d) => setForm({ ...form, last_date: d })} /></div>
              </div>
              <div className="col-span-2">
                <label className="text-xs uppercase tracking-widest text-zinc-500">{t("show_result_to_candidate")}</label>
                <div className="flex gap-2 mt-2">
                  {[true, false].map((v) => (
                    <button key={String(v)} type="button" onClick={() => setForm({ ...form, show_result: v })}
                      className={`px-4 py-2 rounded-full border text-sm ${form.show_result === v ? "bg-[var(--ink)] text-white border-[var(--ink)]" : "border-[var(--line)]"}`}>
                      {v ? t("yes") : t("no")}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-6 justify-end">
              <button onClick={() => setOpen(false)} className="btn-ghost">{t("cancel")}</button>
              <button data-testid="emp-save" onClick={save} className="btn-primary">{t("save")}</button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto" onClick={() => setBulkOpen(false)}>
          <div className="card-clean p-8 w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-2xl font-bold mb-2">{t("bulk_import")}</h3>
            <p className="text-zinc-500 text-sm mb-4">Required columns: first_name, last_name, email, from_page, to_page, start_date (YYYY-MM-DD), last_date (YYYY-MM-DD). Optional: password, show_result.</p>
            <div className="flex gap-3 flex-wrap">
              <button onClick={downloadTemplate} className="btn-ghost"><Download size={14} /> {t("download_template")}</button>
              <label className="btn-primary cursor-pointer">
                <Upload size={14} /> {t("upload_csv")}
                <input ref={fileRef} type="file" accept=".csv" data-testid="bulk-csv-file" className="hidden" onChange={handleUpload} />
              </label>
            </div>
            {bulkResult && (
              <div className="mt-6 text-sm space-y-2">
                <div className="card-clean p-4 bg-green-50 border-green-200">
                  <strong>Created: {bulkResult.created_count}</strong>
                  {bulkResult.created?.length > 0 && (
                    <ul className="mt-2 text-xs font-mono space-y-1 max-h-40 overflow-y-auto">
                      {bulkResult.created.map((c) => <li key={c.email}>{c.email} → {c.password}</li>)}
                    </ul>
                  )}
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="card-clean p-4 bg-red-50 border-red-200">
                    <strong>Errors: {bulkResult.errors.length}</strong>
                    <ul className="mt-2 text-xs space-y-1 max-h-40 overflow-y-auto">
                      {bulkResult.errors.map((e) => <li key={`${e.row}-${e.error}`}>Row {e.row}: {e.error}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end mt-6">
              <button onClick={() => setBulkOpen(false)} className="btn-ghost">{t("close")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
